import type { ProxyResponse, SeoPlugin, PageBuilder } from "../types";

// How a page is currently built, for the Quick Edit Sitemap "Editor Type" column.
// "other" = a non-Elementor third-party builder (Divi, Beaver, Bricks, Oxygen, WPBakery, …).
export type EditorType = "elementor" | "other" | "block" | "classic" | "classic-markdown";

const PROXY = "/api/wp";

async function call(method: "GET"|"POST"|"DELETE", url: string, auth: string, body?: Record<string,unknown>): Promise<ProxyResponse> {
  const res = await fetch(PROXY, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ method, url, auth, body }) });
  if (!res.ok) throw new Error(`Proxy ${res.status}: ${await res.text().then(t => t.slice(0,100))}`);
  return res.json();
}

// Decode HTML entities (and strip any tags) from a rendered WP title, preserving case.
function decodeHtmlText(s: string): string {
  try { return (new DOMParser().parseFromString(s, "text/html").body.textContent ?? s).trim(); }
  catch { return s.replace(/<[^>]+>/g, "").trim(); }
}

// Normalize a title for duplicate comparison: decode HTML entities, strip tags, drop
// punctuation, lowercase, and collapse whitespace.
function normalizeTitle(s: string): string {
  let t = s;
  try { t = new DOMParser().parseFromString(s, "text/html").body.textContent ?? s; } catch { /* keep raw */ }
  return t.toLowerCase().replace(/<[^>]+>/g, " ").replace(/[^\w\s]/g, " ").replace(/\s+/g, " ").trim();
}

// Resolve a user-entered site URL to its canonical origin (server follows redirects). Returns
// null on failure so callers fall back to the entered URL. Used at connect time so a www/non-www
// or http/https variation doesn't break auth (a cross-origin redirect strips the auth header).
export async function resolveSiteUrl(url: string): Promise<string | null> {
  try {
    const res = await fetch("/api/wp/resolve", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ url }) });
    const data = await res.json() as { ok?: boolean; url?: string };
    return data.ok && data.url ? data.url : null;
  } catch { return null; }
}

// A redirect row from the LSG plugin's /seo-audit/v1/redirects endpoint.
export interface LsgRedirect {
  id: number;
  source: string;        // normalized source path (e.g. /old-parent/child)
  destination: string;   // full destination URL
  status: number;        // 301 | 302
  enabled: boolean;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export function buildClient(siteUrl: string, username: string, appPassword: string) {
  const base = siteUrl.replace(/\/$/, "");
  const auth = "Basic " + btoa(`${username}:${appPassword.replace(/\s/g, "")}`);
  const get = (path: string) => call("GET", `${base}${path}`, auth);
  const post = (path: string, body: Record<string,unknown>) => call("POST", `${base}${path}`, auth, body);
  const del = (path: string) => call("DELETE", `${base}${path}`, auth);

  return {
    async testAuth() {
      // context=edit is REQUIRED for the `roles` field — WordPress omits it in the default
      // view context, so without this every user would look role-less.
      const r = await get("/wp-json/wp/v2/users/me?context=edit&_fields=id,name,roles");
      if (r.success) return { ok: true, user: r.data as { name: string; roles: string[] } };
      const wpMsg = (r.data as { message?: string })?.message;
      // 401 = the request reached WordPress but the login was rejected. The most common
      // site-specific cause is the host stripping the Authorization header (so WP never sees the
      // App Password); also possible: Application Passwords disabled, or a wrong username/password.
      if (r.status === 401) {
        return { ok: false, error: `${wpMsg ?? "Not authorized"} — the site received the request but rejected the login. This usually means the host is stripping the Authorization header (so WordPress never sees your Application Password), Application Passwords are disabled on the site, or the username / Application Password is wrong. Updating the Local SEO Garage plugin to v1.16+ fixes the header-stripping case automatically; otherwise verify the App Password and that Application Passwords are enabled.` };
      }
      return { ok: false, error: wpMsg ?? r.error ?? "Auth failed" };
    },
    async detectPlugin(): Promise<SeoPlugin> {
      // 1) Most reliable: the REST root lists each plugin's registered namespace,
      //    present even when the plugin's meta keys aren't exposed on posts.
      try {
        const root = await get("/wp-json/");
        const ns = ((root.data as { namespaces?: string[] })?.namespaces ?? []).map(n => n.toLowerCase());
        if (ns.some(n => n.startsWith("rankmath"))) return "rankmath";
        if (ns.some(n => n.startsWith("yoast")))    return "yoast";
        if (ns.some(n => n.includes("aioseo")))     return "aioseo";
        if (ns.some(n => n.startsWith("seopress"))) return "seopress";
      } catch { /* fall through to meta-key sniffing */ }

      // 2) Fallback: sniff a page's meta keys (only works if keys are REST-registered).
      const r = await get("/wp-json/wp/v2/pages?per_page=1&_fields=id,meta");
      const keys = Object.keys((r.data as {meta?:Record<string,string>}[])?.[0]?.meta ?? {});
      if (keys.includes("rank_math_title"))            return "rankmath";
      if (keys.some(k => k.includes("yoast")))         return "yoast";
      if (keys.some(k => k.startsWith("_aioseo")))     return "aioseo";
      if (keys.some(k => k.startsWith("_seopress")))   return "seopress";
      return "unknown";
    },
    async getSiteTitle(): Promise<string> {
      const r = await get("/wp-json/");
      return (r.data as {name?:string})?.name ?? "";
    },
    // Best-effort SITE-WIDE page builder detection (for the connection status line).
    // This is only a label — per-page routing at edit time is what actually matters,
    // since a single page may or may not be built with the active builder.
    async detectPageBuilder(): Promise<PageBuilder> {
      // 1) REST namespaces — works without our helper plugin.
      try {
        const root = await get("/wp-json/");
        const ns = ((root.data as { namespaces?: string[] })?.namespaces ?? []).map(n => n.toLowerCase());
        if (ns.some(n => n.startsWith("elementor"))) return "elementor";
        if (ns.some(n => n.startsWith("bricks")))    return "bricks";
      } catch { /* fall through */ }
      // 2) Meta sniff across a sample of pages (the builder-specific keys only appear
      //    once REST-registered — e.g. our helper exposes _elementor_data/_edit_mode).
      try {
        const r = await get("/wp-json/wp/v2/pages?per_page=20&context=edit&_fields=id,meta");
        const metas = ((r.data as Array<{ meta?: Record<string, unknown> }> | null) ?? []).map(p => p.meta ?? {});
        if (metas.some(m => m["_elementor_edit_mode"] === "builder" || m["_elementor_data"])) return "elementor";
        if (metas.some(m => m["_et_pb_use_builder"] === "on"))   return "divi";
        if (metas.some(m => m["_fl_builder_enabled"]))           return "beaver";
        if (metas.some(m => m["_bricks_page_content_2"]))        return "bricks";
      } catch { /* fall through */ }
      return "gutenberg";
    },
    // Per-page builder model — decides, for THIS specific page, whether its content
    // lives in the normal editor or in Elementor (never assumes from the site-wide
    // builder). Prefers the helper plugin's builder-aware route (which reads Elementor
    // via its own document API); falls back to core REST for classic pages.
    async getBuilderModel(type: "pages" | "posts", id: number): Promise<{ builder: "elementor" | "classic"; elements: unknown[] | null; content: string | null; viaHelper: boolean }> {
      try {
        const r = await get(`/wp-json/seo-audit/v1/builder/${id}`);
        const d = r.data as { builder?: string; elements?: unknown[]; content?: string } | null;
        if (r.success && d && (d.builder === "elementor" || d.builder === "classic")) {
          return { builder: d.builder, elements: Array.isArray(d.elements) ? d.elements : null, content: typeof d.content === "string" ? d.content : null, viaHelper: true };
        }
      } catch { /* helper not installed — fall back to core REST */ }
      const r = await get(`/wp-json/wp/v2/${type}/${id}?context=edit&_fields=id,content,meta`);
      const d = r.data as { content?: { raw?: string }; meta?: Record<string, unknown> } | null;
      const raw = typeof d?.content?.raw === "string" ? d.content.raw : null;
      const mode = d?.meta?.["_elementor_edit_mode"];
      const isElementor = mode === "builder" || (!raw && !!d?.meta?.["_elementor_data"]);
      return { builder: isElementor ? "elementor" : "classic", elements: null, content: raw, viaHelper: false };
    },
    // Save an edited Elementor element tree via the helper route, which calls
    // Elementor's own document save (correct storage format + cache regeneration).
    async saveElementorElements(id: number, elements: unknown[]) {
      return post(`/wp-json/seo-audit/v1/elementor/${id}`, { elements });
    },
    // Definitive REST-writability check for an SEO meta key: create a throwaway
    // DRAFT page with a sentinel value, read it back, then delete the draft.
    // Never touches live content. Returns false if the key isn't accepted/registered.
    async verifyMetaWritable(metaKey: string): Promise<boolean> {
      if (!metaKey) return false;
      const sentinel = "seo-audit-rest-write-test";
      const created = await post("/wp-json/wp/v2/pages", {
        title: "Local SEO Garage — REST write test (safe to delete)",
        status: "draft",
        meta: { [metaKey]: sentinel },
      });
      const id = (created.data as { id?: number })?.id;
      if (!created.success || !id) return false;
      try {
        const r = await get(`/wp-json/wp/v2/pages/${id}?context=edit&_fields=meta`);
        const meta = (r.data as { meta?: Record<string, unknown> })?.meta ?? {};
        return meta[metaKey] === sentinel;
      } finally {
        try { await del(`/wp-json/wp/v2/pages/${id}?force=true`); } catch { /* best-effort cleanup */ }
      }
    },
    async updatePage(id: number, body: Record<string,unknown>) { return post(`/wp-json/wp/v2/pages/${id}`, body); },
    // Update either a page or a post (the REST endpoint differs by type).
    async updatePageOrPost(type: "page" | "post", id: number, body: Record<string,unknown>) {
      return post(`/wp-json/wp/v2/${type === "post" ? "posts" : "pages"}/${id}`, body);
    },

    // ── All in One SEO meta (via the LSG plugin) ───────────────────────────────
    // AIOSEO stores the SEO title/description in its own table (wp_aioseo_posts), not post
    // meta, so they can't be written through the core REST `meta` field. The LSG plugin
    // exposes this route to write them through AIOSEO's own model.
    async saveAioseoMeta(id: number, fields: { title?: string; description?: string; canonical?: string }) {
      return post(`/wp-json/seo-audit/v1/aioseo-meta/${id}`, fields);
    },
    // Read AIOSEO's current SEO title/description/canonical (from its own table) via the LSG
    // plugin's GET route, for pre-filling the Edit Page dialog. Returns empty strings when the
    // route is missing (older plugin) or AIOSEO has no row for the post yet.
    async getAioseoMeta(id: number): Promise<{ title: string; description: string; canonical: string }> {
      try {
        const r = await get(`/wp-json/seo-audit/v1/aioseo-meta/${id}`);
        const d = r.data as { title?: string; description?: string; canonical?: string } | null;
        return { title: d?.title ?? "", description: d?.description ?? "", canonical: d?.canonical ?? "" };
      } catch { return { title: "", description: "", canonical: "" }; }
    },
    // True only when AIOSEO is active AND the LSG plugin's aioseo-meta route exists: write a
    // sentinel title to a throwaway draft via that route and confirm it saved (then delete).
    async verifyAioseoMetaWritable(): Promise<boolean> {
      const sentinel = "seo-audit-rest-write-test";
      const created = await post("/wp-json/wp/v2/pages", {
        title: "Local SEO Garage — AIOSEO write test (safe to delete)",
        status: "draft",
      });
      const id = (created.data as { id?: number })?.id;
      if (!created.success || !id) return false;
      try {
        const r = await post(`/wp-json/seo-audit/v1/aioseo-meta/${id}`, { title: sentinel });
        const d = r.data as { ok?: boolean; title?: string } | null;
        return !!r.success && !!d?.ok && d.title === sentinel;
      } finally {
        try { await del(`/wp-json/wp/v2/pages/${id}?force=true`); } catch { /* best-effort cleanup */ }
      }
    },

    // ── Schema injection via the SEO Audit helper (functions.php snippet or plugin) ──
    // The helper registers `_audit_schema` (an array of JSON-LD strings) for REST and
    // prints each block in wp_head, so schema is added in <head> on ANY page builder,
    // additively — never touching content or the page's existing schema.

    // True only when the helper is installed: it's the one thing that registers
    // `_audit_schema` for REST. We confirm by writing a sentinel block to a throwaway
    // draft and reading it back (then deleting the draft). Never touches live content.
    async isSchemaHelperInstalled(): Promise<boolean> {
      const sentinel = JSON.stringify({ "@context": "https://schema.org", "@type": "Thing", "name": "seo-audit-helper-probe" });
      const created = await post("/wp-json/wp/v2/pages", {
        title: "Local SEO Garage — plugin check (safe to delete)",
        status: "draft",
        meta: { _audit_schema: [sentinel] },
      });
      const id = (created.data as { id?: number })?.id;
      if (!created.success || !id) return false;
      try {
        const r = await get(`/wp-json/wp/v2/pages/${id}?context=edit&_fields=meta`);
        const v = (r.data as { meta?: { _audit_schema?: unknown } })?.meta?._audit_schema;
        return Array.isArray(v) && v.includes(sentinel);
      } finally {
        try { await del(`/wp-json/wp/v2/pages/${id}?force=true`); } catch { /* best-effort cleanup */ }
      }
    },
    // Current JSON-LD blocks stored on a page by the helper (each element is a JSON string).
    async getAuditSchema(pageId: number, type: "pages" | "posts" = "pages"): Promise<string[]> {
      const r = await get(`/wp-json/wp/v2/${type}/${pageId}?context=edit&_fields=meta`);
      const v = (r.data as { meta?: { _audit_schema?: unknown } })?.meta?._audit_schema;
      return Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : [];
    },
    async setAuditSchema(pageId: number, blocks: string[], type: "pages" | "posts" = "pages") {
      // Stamp a durable "schema added/updated" timestamp alongside the blocks so the History –
      // Published Schema view can show a true date (not just the page's last-modified proxy).
      // Cleared to "" when schema is removed. Requires the plugin's `_audit_schema_ts` meta;
      // older sites without it simply ignore the extra key and fall back to `modified`.
      const ts = blocks.length ? new Date().toISOString() : "";
      return post(`/wp-json/wp/v2/${type}/${pageId}`, { meta: { _audit_schema: blocks, _audit_schema_ts: ts } });
    },
    // Stamp a durable "optimized by the Low-Hanging Keyword tool" ISO timestamp on the page, so the
    // record lives on the page itself (survives URL/slug changes, visible in WP) — parity with the
    // _lsg_tool_created / _audit_schema markers. Requires the plugin's `_lsg_lhk_optimized` meta;
    // older sites without it simply ignore the key (WordPress drops unregistered protected meta).
    async markLhkOptimized(id: number, type: "pages" | "posts" = "pages") {
      return post(`/wp-json/wp/v2/${type}/${id}`, { meta: { _lsg_lhk_optimized: new Date().toISOString() } });
    },
    // The site's front-page ID (for homepage schema), or null when the front page
    // is the blog index (no editable Page to attach meta to). NOTE: the settings
    // endpoint needs the Administrator (manage_options) capability — for non-admin
    // users this returns null and callers should fall back to getPageIdByUrl().
    async getFrontPageId(): Promise<number | null> {
      const r = await get("/wp-json/wp/v2/settings");
      const onFront = (r.data as { page_on_front?: number; show_on_front?: string } | null);
      if (onFront?.show_on_front === "page" && onFront.page_on_front) return onFront.page_on_front;
      return null;
    },
    // Resolve a published page's ID from its public URL. Works for ANY user who can
    // read pages (no admin needed), so it resolves the homepage even when the
    // settings endpoint is forbidden — a static front page's link is the site root.
    async getPageIdByUrl(url: string): Promise<number | null> {
      if (!url) return null;
      const norm = (u: string) => u.replace(/[#?].*$/, "").replace(/\/+$/, "").toLowerCase();
      const target = norm(url);
      for (let page = 1; page <= 5; page++) {
        const r = await get(`/wp-json/wp/v2/pages?per_page=100&page=${page}&_fields=id,link`);
        const arr = r.data as Array<{ id: number; link: string }> | null;
        if (!Array.isArray(arr) || !arr.length) break;
        const match = arr.find(p => norm(p.link || "") === target);
        if (match) return match.id;
        if (arr.length < 100) break;
      }
      return null;
    },
    async getPageContent(id: number) {
      const r = await get(`/wp-json/wp/v2/pages/${id}?_fields=id,content`);
      return (r.data as {content?:{raw:string}})?.content ?? null;
    },
    // Raw (editable) HTML body of a page or post — context=edit is required for
    // content.raw to be present. Returns null if not available (e.g. page builder).
    async getRawContent(type: "pages" | "posts", id: number): Promise<string | null> {
      const r = await get(`/wp-json/wp/v2/${type}/${id}?context=edit&_fields=id,content`);
      const raw = (r.data as { content?: { raw?: string } })?.content?.raw;
      return typeof raw === "string" ? raw : null;
    },
    // Rendered (published) HTML of a page/post. Used as a read-only fallback when
    // content.raw is empty — page builders (Elementor/Divi/Bricks/WPBakery) store their
    // markup in postmeta, not post_content, but their output is injected via the_content,
    // which context=view applies. Works for any reader (no edit rights needed).
    async getRenderedContent(type: "pages" | "posts", id: number): Promise<string | null> {
      const r = await get(`/wp-json/wp/v2/${type}/${id}?_fields=id,content`);
      const rendered = (r.data as { content?: { rendered?: string } })?.content?.rendered;
      return typeof rendered === "string" ? rendered : null;
    },
    // Overwrite the body HTML of a page or post.
    async updateContent(type: "pages" | "posts", id: number, html: string) {
      return post(`/wp-json/wp/v2/${type}/${id}`, { content: html });
    },
    // Which editor the site uses sitewide — so created content is posted in the right
    // format (Gutenberg block markup vs plain HTML for Classic).
    async detectEditor(): Promise<"block" | "classic"> {
      // Authoritative: the helper plugin reports use_block_editor_for_post_type().
      try {
        const r = await get("/wp-json/seo-audit/v1/editor");
        const d = r.data as { editor?: string } | null;
        if (r.success && (d?.editor === "block" || d?.editor === "classic")) return d.editor;
      } catch { /* helper not installed — fall back */ }
      // Heuristic: do recent pages contain Gutenberg block comments?
      try {
        const r = await get("/wp-json/wp/v2/pages?per_page=10&context=edit&_fields=id,content");
        const arr = r.data as Array<{ content?: { raw?: string } }> | null;
        if (Array.isArray(arr) && arr.length) {
          return arr.some(p => typeof p.content?.raw === "string" && p.content.raw.includes("<!-- wp:")) ? "block" : "classic";
        }
      } catch { /* ignore */ }
      return "block"; // modern WP default; block markup also renders fine on Classic sites
    },
    // Resolve a published post (article) by slug → { id, link }.
    async getPostBySlug(slug: string): Promise<{ id: number; link: string } | null> {
      const r = await get(`/wp-json/wp/v2/posts?slug=${encodeURIComponent(slug)}&_fields=id,link&per_page=5`);
      const arr = r.data as Array<{ id: number; link: string }> | null;
      if (!Array.isArray(arr) || !arr.length) return null;
      return { id: arr[0].id, link: arr[0].link };
    },
    async getPageBySlug(slug: string): Promise<{ id: number; slug: string; link: string; tags: number[] } | null> {
      const r = await get(`/wp-json/wp/v2/pages?slug=${encodeURIComponent(slug)}&_fields=id,slug,link,tags&per_page=5`);
      const arr = r.data as Array<{ id: number; slug: string; link: string; tags?: number[] }> | null;
      if (!Array.isArray(arr) || !arr.length) return null;
      const p = arr[0];
      return { id: p.id, slug: p.slug, link: p.link, tags: Array.isArray(p.tags) ? p.tags : [] };
    },
    // Rendered title of a single page/post (used by the LHK history record).
    async getTitle(type: "pages" | "posts", id: number): Promise<string> {
      const r = await get(`/wp-json/wp/v2/${type}/${id}?_fields=title`);
      const t = (r.data as { title?: { rendered?: string } } | null)?.title?.rendered ?? "";
      return decodeHtmlText(t);
    },
    // Every published page + post (no images/attachments) with its WP id, title, URL and tag
    // NAMES — the authoritative page inventory for the Sitemap tab. Tag IDs are resolved to
    // names with one paginated /tags fetch (not per page).
    async getAllPagesAndPosts(): Promise<Array<{ id: number; title: string; url: string; type: "page" | "post"; tags: string[]; parent: number; slug: string; auditSchema: string[]; modified: string; schemaTs: string; createdTs: string }>> {
      const fetchType = async (type: "pages" | "posts") => {
        const out: Array<{ id: number; title: string; url: string; tagIds: number[]; parent: number; slug: string; auditSchema: string[]; modified: string; schemaTs: string; createdTs: string }> = [];
        const seen = new Set<number>();
        let error: string | null = null;
        // Paginate until an empty/error page. Do NOT stop when a page returns fewer than the
        // requested 100 — some hosts/security plugins cap REST per_page below 100, and a naive
        // "< 100 → stop" heuristic drops every page/post after the first short page.
        for (let page = 1; page <= 300; page++) {
          // context=edit + meta._audit_schema so each row knows whether the LSG tool added schema
          // (drives the Sitemap Schema column). parent/slug are only meaningful for pages.
          // _lsg_tool_created / _lsg_service_area mark pages the tool CREATED (new pages, service-area
          // pages) — those got their schema baked in at creation, not added later to a live page. The
          // History view uses that to separate "schema you added via the Schema/Express tools" from
          // "schema that shipped with a tool-created page".
          const r = await get(`/wp-json/wp/v2/${type}?per_page=100&page=${page}&context=edit&_fields=id,title,link,tags,parent,slug,modified,meta._audit_schema,meta._audit_schema_ts,meta._lsg_tool_created,meta._lsg_service_area`);
          // A failed request (401/403/500 — e.g. the account lacks edit rights, or a security
          // plugin blocks authenticated REST) must NOT masquerade as "no pages": record the real
          // error on the first page so the caller can surface it instead of a silent empty result.
          if (!r.success) {
            if (page === 1) { const m = (r.data as { message?: string } | null)?.message; error = `HTTP ${r.status}${m ? ` — ${m}` : r.error ? ` — ${r.error}` : ""}`; }
            break;
          }
          const arr = r.data as Array<{ id: number; title?: { rendered?: string }; link?: string; tags?: number[]; parent?: number; slug?: string; modified?: string; meta?: { _audit_schema?: unknown; _audit_schema_ts?: unknown; _lsg_tool_created?: unknown; _lsg_service_area?: unknown } }> | null;
          // A 200 that ISN'T a JSON list (a login/maintenance page, a redirect, a cache/CDN wall,
          // or a security-plugin challenge intercepting /wp-json) — surface it with a snippet of the
          // actual body so the real problem is visible instead of a silent "no pages".
          if (!Array.isArray(arr)) {
            if (page === 1) {
              const snip = typeof r.data === "string" ? r.data.replace(/\s+/g, " ").trim().slice(0, 140) : JSON.stringify(r.data ?? {}).slice(0, 140);
              error = `HTTP ${r.status} but the response was not a ${type} list — a login page, redirect, cache, or security layer may be intercepting the REST API. Response starts: ${snip}`;
            }
            break;
          }
          if (arr.length === 0) break;
          let added = 0;
          for (const p of arr) {
            if (typeof p.id !== "number" || seen.has(p.id)) continue;
            seen.add(p.id);
            // ISO creation time for tool-created pages ("" for pre-existing pages). _lsg_tool_created
            // and _lsg_service_area are stamped with the same value at creation; prefer either.
            const createdTs = (typeof p.meta?._lsg_tool_created === "string" && p.meta._lsg_tool_created) || (typeof p.meta?._lsg_service_area === "string" && p.meta._lsg_service_area) || "";
            out.push({ id: p.id, title: decodeHtmlText(p.title?.rendered ?? ""), url: p.link ?? "", tagIds: Array.isArray(p.tags) ? p.tags : [], parent: typeof p.parent === "number" ? p.parent : 0, slug: p.slug ?? "", modified: typeof p.modified === "string" ? p.modified : "", schemaTs: typeof p.meta?._audit_schema_ts === "string" ? p.meta._audit_schema_ts : "", createdTs, auditSchema: Array.isArray(p.meta?._audit_schema) ? (p.meta!._audit_schema as unknown[]).filter((x): x is string => typeof x === "string") : [] });
            added++;
          }
          if (added === 0) break; // server ignored ?page (all dupes) — stop rather than loop
        }
        return { out, error };
      };
      const [pagesR, postsR] = await Promise.all([fetchType("pages"), fetchType("posts")]);
      const pages = pagesR.out, posts = postsR.out;
      // If BOTH came back empty AND at least one request errored, it's an access problem, not an
      // empty site — throw the real reason so the crawl reports it (not the generic "no pages").
      if (!pages.length && !posts.length && (pagesR.error || postsR.error)) {
        throw new Error(`WordPress rejected the page/post request (${pagesR.error ?? postsR.error}). The connected account may lack edit permissions, or a security plugin / host firewall is blocking authenticated REST API requests.`);
      }

      // Resolve tag IDs → names once.
      const needTags = [...pages, ...posts].some(p => p.tagIds.length);
      const tagMap = new Map<number, string>();
      if (needTags) {
        for (let page = 1; page <= 300; page++) {
          const r = await get(`/wp-json/wp/v2/tags?per_page=100&page=${page}&_fields=id,name`);
          const arr = r.data as Array<{ id: number; name: string }> | null;
          if (!Array.isArray(arr) || arr.length === 0) break;
          const before = tagMap.size;
          arr.forEach(t => tagMap.set(t.id, decodeHtmlText(t.name)));
          if (tagMap.size === before) break; // no new tags — server ignored ?page
        }
      }
      const mapTags = (ids: number[]) => ids.map(id => tagMap.get(id)).filter((n): n is string => !!n);
      return [
        ...pages.map(p => ({ id: p.id, title: p.title, url: p.url, type: "page" as const, tags: mapTags(p.tagIds), parent: p.parent, slug: p.slug, auditSchema: p.auditSchema, modified: p.modified, schemaTs: p.schemaTs, createdTs: p.createdTs })),
        ...posts.map(p => ({ id: p.id, title: p.title, url: p.url, type: "post" as const, tags: mapTags(p.tagIds), parent: p.parent, slug: p.slug, auditSchema: p.auditSchema, modified: p.modified, schemaTs: p.schemaTs, createdTs: p.createdTs })),
      ];
    },
    // Fresh title/URL/tag-names (+ LSG _audit_schema blocks) for a single page or post (Sitemap
    // per-row refresh). Returns null if it no longer exists (deleted).
    async getOnePageOrPost(type: "page" | "post", id: number): Promise<{ id: number; title: string; url: string; tags: string[]; parent: number; slug: string; auditSchema: string[] } | null> {
      const ep = type === "post" ? "posts" : "pages";
      const r = await get(`/wp-json/wp/v2/${ep}/${id}?context=edit&_fields=id,title,link,tags,parent,slug,meta._audit_schema`);
      const p = r.data as { id?: number; title?: { rendered?: string }; link?: string; tags?: number[]; parent?: number; slug?: string; meta?: { _audit_schema?: unknown } } | null;
      if (!p?.id) return null;
      const ids = Array.isArray(p.tags) ? p.tags : [];
      let tags: string[] = [];
      if (ids.length) {
        const tr = await get(`/wp-json/wp/v2/tags?include=${ids.join(",")}&per_page=100&_fields=id,name`);
        const arr = tr.data as Array<{ id: number; name: string }> | null;
        const m = new Map((arr ?? []).map(t => [t.id, decodeHtmlText(t.name)]));
        tags = ids.map(i => m.get(i)).filter((n): n is string => !!n);
      }
      const auditSchema = Array.isArray(p.meta?._audit_schema) ? (p.meta!._audit_schema as unknown[]).filter((x): x is string => typeof x === "string") : [];
      return { id: p.id, title: decodeHtmlText(p.title?.rendered ?? ""), url: p.link ?? "", tags, parent: typeof p.parent === "number" ? p.parent : 0, slug: p.slug ?? "", auditSchema };
    },
    // Current publish status, parent, categories, and raw SEO post-meta for a single page/post
    // (context=edit), for pre-filling the Edit Page dialog. `meta` holds the SEO plugin's raw
    // keys (e.g. the canonical URL) for RankMath/Yoast/SEOPress; AIOSEO keeps its data elsewhere
    // (getAioseoMeta). `categories` is only meaningful for posts, `parent` only for pages.
    async getPageStatusAndMeta(type: "page" | "post", id: number): Promise<{ status: string; parent: number; categories: number[]; meta: Record<string, unknown> }> {
      const ep = type === "post" ? "posts" : "pages";
      const r = await get(`/wp-json/wp/v2/${ep}/${id}?context=edit&_fields=id,status,parent,categories,meta`);
      const d = r.data as { status?: string; parent?: number; categories?: number[]; meta?: Record<string, unknown> } | null;
      return {
        status: typeof d?.status === "string" ? d.status : "publish",
        parent: typeof d?.parent === "number" ? d.parent : 0,
        categories: Array.isArray(d?.categories) ? d!.categories.filter((n): n is number => typeof n === "number") : [],
        meta: d?.meta ?? {},
      };
    },
    // Resolve tag IDs → names (preserving input order).
    async getTagNames(ids: number[]): Promise<string[]> {
      if (!ids.length) return [];
      const r = await get(`/wp-json/wp/v2/tags?include=${ids.join(",")}&per_page=100&_fields=id,name`);
      const arr = r.data as Array<{ id: number; name: string }> | null;
      if (!Array.isArray(arr)) return [];
      const byId = new Map(arr.map(t => [t.id, t.name]));
      return ids.map(id => byId.get(id)).filter((n): n is string => !!n);
    },
    // Duplicate guard: find an existing published/draft page OR post that matches the
    // given slug (exact) or title (exact, case/punctuation-insensitive). Checks BOTH
    // pages and posts so a duplicate is caught regardless of which type it was created
    // as. Returns the first match (slug takes priority), or null if none.
    async findExistingByTitleOrSlug(opts: { slug: string; title: string }):
      Promise<{ id: number; link: string; title: string; matchedBy: "slug" | "title"; type: "page" | "post"; createdByTool?: string } | null> {
      const slug = opts.slug.trim();
      const titleNorm = normalizeTitle(opts.title);
      const eps: Array<{ ep: string; t: "page" | "post" }> = [{ ep: "pages", t: "page" }, { ep: "posts", t: "post" }];
      type Row = { id: number; link: string; title?: { rendered?: string }; meta?: { _lsg_tool_created?: string } };
      const toMatch = (r: Row, t: "page" | "post", by: "slug" | "title") =>
        ({ id: r.id, link: r.link, title: (r.title?.rendered ?? "").trim(), matchedBy: by, type: t, createdByTool: r.meta?._lsg_tool_created });
      // 1) Exact slug match (most reliable — same suggestion ⇒ same slug).
      if (slug) {
        for (const { ep, t } of eps) {
          const r = await get(`/wp-json/wp/v2/${ep}?slug=${encodeURIComponent(slug)}&_fields=id,link,title,meta&context=edit&per_page=5`);
          const arr = r.data as Row[] | null;
          if (Array.isArray(arr) && arr.length) return toMatch(arr[0], t, "slug");
        }
      }
      // 2) Exact title match (catches the same page created under a different slug).
      if (titleNorm) {
        for (const { ep, t } of eps) {
          const r = await get(`/wp-json/wp/v2/${ep}?search=${encodeURIComponent(opts.title)}&_fields=id,link,title,meta&context=edit&per_page=20`);
          const arr = r.data as Row[] | null;
          const hit = Array.isArray(arr) ? arr.find(p => normalizeTitle(p.title?.rendered ?? "") === titleNorm) : undefined;
          if (hit) return toMatch(hit, t, "title");
        }
      }
      return null;
    },
    // List every page/post created by the tool — i.e. carrying the "LSG Tool" tag.
    // Returns the creation timestamp from the _lsg_tool_created meta when present.
    // (For a future "pages created by the tool" tab.)
    async getToolCreatedPages():
      Promise<Array<{ id: number; title: string; link: string; type: "page" | "post"; created?: string }>> {
      const tagRes = await get(`/wp-json/wp/v2/tags?search=${encodeURIComponent("LSG Tool")}&_fields=id,name&per_page=10`);
      const tagArr = tagRes.data as Array<{ id: number; name: string }> | null;
      const tag = Array.isArray(tagArr) ? tagArr.find(t => t.name.toLowerCase() === "lsg tool") : undefined;
      if (!tag) return [];
      const out: Array<{ id: number; title: string; link: string; type: "page" | "post"; created?: string }> = [];
      const eps: Array<{ ep: string; t: "page" | "post" }> = [{ ep: "pages", t: "page" }, { ep: "posts", t: "post" }];
      for (const { ep, t } of eps) {
        const r = await get(`/wp-json/wp/v2/${ep}?tags=${tag.id}&per_page=100&_fields=id,link,title,meta&context=edit`);
        const arr = r.data as Array<{ id: number; link: string; title?: { rendered?: string }; meta?: { _lsg_tool_created?: string } }> | null;
        if (Array.isArray(arr)) for (const p of arr) {
          out.push({ id: p.id, title: (p.title?.rendered ?? "").trim(), link: p.link, type: t, created: p.meta?._lsg_tool_created });
        }
      }
      return out;
    },
    // History list for the Content Drawer: scan the site by the durable _lsg_tool_created
    // meta via the helper plugin's route (robust — survives tag removal). Falls back to the
    // tag-based scan when the v1.5.0+ plugin route isn't available.
    async listLsgPages(): Promise<Array<{ id: number; title: string; link: string; type: "page" | "post"; created?: string }>> {
      try {
        const r = await get("/wp-json/seo-audit/v1/lsg-pages");
        const arr = r.data as Array<{ id: number; title?: string; link: string; type: string; created?: string }> | null;
        if (Array.isArray(arr)) {
          return arr.map(p => ({
            id: p.id,
            title: (p.title ?? "").trim(),
            link: p.link,
            type: p.type === "post" ? "post" : "page",
            created: p.created,
          }));
        }
      } catch { /* route missing (older plugin) — fall back below */ }
      return this.getToolCreatedPages();
    },
    // History for the Service Area Buildout tab: scan by the durable _lsg_service_area meta via
    // the plugin route (survives tag removal). Falls back to the visible "LSG Service Area Page"
    // tag when the v1.13.0+ route isn't available yet.
    async listServiceAreaPages(): Promise<Array<{ id: number; title: string; link: string; type: "page" | "post"; created?: string }>> {
      try {
        const r = await get("/wp-json/seo-audit/v1/service-area-pages");
        const arr = r.data as Array<{ id: number; title?: string; link: string; type: string; created?: string }> | null;
        if (Array.isArray(arr)) {
          return arr.map(p => ({ id: p.id, title: (p.title ?? "").trim(), link: p.link, type: p.type === "post" ? "post" : "page", created: p.created }));
        }
      } catch { /* route missing — fall back to the tag scan below */ }
      const tagRes = await get(`/wp-json/wp/v2/tags?search=${encodeURIComponent("LSG Service Area Page")}&_fields=id,name&per_page=10`);
      const tagArr = tagRes.data as Array<{ id: number; name: string }> | null;
      const tag = Array.isArray(tagArr) ? tagArr.find(t => t.name.toLowerCase() === "lsg service area page") : undefined;
      if (!tag) return [];
      const out: Array<{ id: number; title: string; link: string; type: "page" | "post"; created?: string }> = [];
      for (const { ep, t } of [{ ep: "pages", t: "page" as const }, { ep: "posts", t: "post" as const }]) {
        const r = await get(`/wp-json/wp/v2/${ep}?tags=${tag.id}&per_page=100&_fields=id,link,title,meta&context=edit`);
        const arr = r.data as Array<{ id: number; link: string; title?: { rendered?: string }; meta?: { _lsg_service_area?: string; _lsg_tool_created?: string } }> | null;
        if (Array.isArray(arr)) for (const p of arr) out.push({ id: p.id, title: (p.title?.rendered ?? "").trim(), link: p.link, type: t, created: p.meta?._lsg_service_area ?? p.meta?._lsg_tool_created });
      }
      return out;
    },
    // History for the Low-Hanging Keyword tab: scan by the durable _lsg_lhk_optimized meta via the
    // plugin route (v1.24.0+). Returns each optimized page's CURRENT permalink + the ISO insertion
    // time, so the Optimized Pages list resolves pages that were later re-slugged. Returns [] when
    // the route is missing (older plugin) — the tab falls back to its own DB history.
    async listLhkOptimizedPages(): Promise<Array<{ id: number; title: string; link: string; type: "page" | "post"; optimized?: string }>> {
      try {
        const r = await get("/wp-json/seo-audit/v1/lhk-optimized-pages");
        const arr = r.data as Array<{ id: number; title?: string; link: string; type: string; optimized?: string }> | null;
        if (Array.isArray(arr)) {
          return arr.map(p => ({ id: p.id, title: (p.title ?? "").trim(), link: p.link, type: p.type === "post" ? "post" : "page", optimized: p.optimized }));
        }
      } catch { /* route missing (older plugin) — caller falls back to its DB history */ }
      return [];
    },
    // ── Redirects (LSG plugin v1.11.0+) — CRUD under /seo-audit/v1/redirects ──────
    // listRedirects returns [] when the route is missing (older plugin) so the UI degrades.
    async listRedirects(): Promise<LsgRedirect[]> {
      try {
        const r = await get("/wp-json/seo-audit/v1/redirects");
        const arr = (r.data as { redirects?: LsgRedirect[] } | null)?.redirects;
        return Array.isArray(arr) ? arr : [];
      } catch { return []; }
    },
    async createRedirect(body: { source: string; destination: string; status: number; createdBy?: string }) {
      return post("/wp-json/seo-audit/v1/redirects", body as unknown as Record<string, unknown>);
    },
    async updateRedirect(id: number, body: { source?: string; destination?: string; status?: number; enabled?: boolean; createdBy?: string }) {
      return post(`/wp-json/seo-audit/v1/redirects/${id}`, body as unknown as Record<string, unknown>);
    },
    async deleteRedirect(id: number) {
      return del(`/wp-json/seo-audit/v1/redirects/${id}`);
    },
    // ── Markdown rendering toggle (LSG plugin v1.24.4+) — /seo-audit/v1/markdown ──
    // `enabled` = whether the plugin renders Markdown pages on this site; `constantOff` = a
    // wp-config LSG_MARKDOWN=false is forcing it off (the toggle can't override that). Returns a
    // safe "unsupported" shape when the route is missing (site on an older plugin).
    async getMarkdownEnabled(): Promise<{ supported: boolean; enabled: boolean; constantOff: boolean }> {
      try {
        const r = await get("/wp-json/seo-audit/v1/markdown");
        if (!r.success || !r.data) return { supported: false, enabled: false, constantOff: false };
        const d = r.data as { enabled?: boolean; constantOff?: boolean };
        return { supported: true, enabled: !!d.enabled, constantOff: !!d.constantOff };
      } catch { return { supported: false, enabled: false, constantOff: false }; }
    },
    async setMarkdownEnabled(enabled: boolean): Promise<{ enabled: boolean; constantOff: boolean }> {
      const r = await post("/wp-json/seo-audit/v1/markdown", { enabled });
      const d = (r.data as { enabled?: boolean; constantOff?: boolean } | null) ?? {};
      return { enabled: !!d.enabled, constantOff: !!d.constantOff };
    },
    // ── Editor type per page (LSG plugin v1.24.6+) — /seo-audit/v1/editor-types ──
    // Bulk-classify pages as elementor / block / classic / classic-markdown for the Quick Edit Sitemap
    // "Editor Type" column. Batched (plugin caps 200 ids/request). Returns {} for any id on an older
    // plugin (route missing) so the column simply shows "—" rather than erroring.
    async getEditorTypes(ids: number[]): Promise<Record<number, EditorType>> {
      const out: Record<number, EditorType> = {};
      const uniq = Array.from(new Set(ids.filter(n => Number.isFinite(n))));
      for (let i = 0; i < uniq.length; i += 100) {
        const batch = uniq.slice(i, i + 100);
        try {
          const r = await get(`/wp-json/seo-audit/v1/editor-types?ids=${batch.join(",")}`);
          const types = (r.data as { types?: Record<string, string> } | null)?.types;
          if (types) for (const [k, v] of Object.entries(types)) out[Number(k)] = v as EditorType;
        } catch { /* older plugin / route missing → leave these ids unset */ }
      }
      return out;
    },
    async createPage(body: Record<string,unknown>) { return post("/wp-json/wp/v2/pages", { status: "draft", ...body }); },
    async createPost(body: Record<string,unknown>) { return post("/wp-json/wp/v2/posts", { status: "draft", ...body }); },
    async getOrCreateTags(names: string[]): Promise<number[]> {
      const ids: number[] = [];
      for (const name of names) {
        const clean = name.trim();
        if (!clean) continue;
        const search = await get(`/wp-json/wp/v2/tags?search=${encodeURIComponent(clean)}&per_page=5`);
        const existing = (search.data as {id:number; name:string}[] | null)?.find(
          t => t.name.toLowerCase() === clean.toLowerCase()
        );
        if (existing) { ids.push(existing.id); continue; }
        const created = await post("/wp-json/wp/v2/tags", { name: clean });
        const newId = (created.data as {id?:number})?.id;
        if (newId) ids.push(newId);
      }
      return ids;
    },
    // Resolve a post Category by name/slug, creating it if it doesn't exist. Returns its
    // term ID, or null on failure. Used to file new posts under a category.
    async getOrCreateCategory(nameOrSlug: string): Promise<number | null> {
      const clean = nameOrSlug.trim();
      if (!clean) return null;
      const search = await get(`/wp-json/wp/v2/categories?search=${encodeURIComponent(clean)}&per_page=10`);
      const lc = clean.toLowerCase();
      const existing = (search.data as { id: number; name: string; slug: string }[] | null)?.find(
        c => c.name.toLowerCase() === lc || c.slug.toLowerCase() === lc,
      );
      if (existing) return existing.id;
      const created = await post("/wp-json/wp/v2/categories", { name: clean });
      return (created.data as { id?: number })?.id ?? null;
    },
    // Every category (id/name/slug) for the "Search Post Category" picker.
    async getCategories(): Promise<Array<{ id: number; name: string; slug: string }>> {
      const out: Array<{ id: number; name: string; slug: string }> = [];
      const seen = new Set<number>();
      for (let page = 1; page <= 100; page++) {
        const r = await get(`/wp-json/wp/v2/categories?per_page=100&page=${page}&_fields=id,name,slug`);
        const arr = r.data as Array<{ id: number; name?: string; slug?: string }> | null;
        if (!Array.isArray(arr) || arr.length === 0) break;
        let added = 0;
        for (const c of arr) { if (seen.has(c.id)) continue; seen.add(c.id); out.push({ id: c.id, name: decodeHtmlText(c.name ?? ""), slug: c.slug ?? "" }); added++; }
        if (added === 0) break;
      }
      return out;
    },
  };
}
export type WPClient = ReturnType<typeof buildClient>;
